import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { makeStyles } from '@mui/styles';
import { Box, Tooltip } from '@mui/material';
import { keyframes } from '@mui/system';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
} from '../../redux/features/config/config.selector';
import {
  selectIsExpressTimerElapsed,
  selectTaskAssignedSessionStartedAt,
} from '../../redux/features/taskRouter/taskRouter.selector';
import { selectIsCarExited } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { setExpressTimerElapsed } from '../../redux/features/taskRouter/taskRouter.slice';
import Colors from '../../utils/color';

const useStyles = makeStyles(() => ({
  container: {
    minWidth: '48px',
    margin: '0px !important',
    textAlign: 'center',
    flexShrink: 0,
  },
}));

// Gentle 1s background pulse for the over-threshold alert state. Declared once
// at module scope (emotion `keyframes`) so it is not re-created per render and
// no `makeStyles` rule is added (keeps the flag-off render and snapshots stable).
const expressTimerPulse = keyframes({
  '0%, 100%': { backgroundColor: Colors.guardsmanRed },
  '50%': { backgroundColor: Colors.bittersweet },
});

// This component will show the duration of the active session
export const Timer = () => {
  const classes = useStyles();
  const sessionId = useAppSelector(selectSessionId);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isExpressTimerEnabled = useAppSelector(
    selectFeatureFlagHitlExpressTimer
  );
  const maxSeconds = useAppSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useAppSelector(selectTaskAssignedSessionStartedAt);
  const alreadyElapsed = useAppSelector(selectIsExpressTimerElapsed);
  const carExited = useAppSelector(selectIsCarExited);
  const dispatch = useAppDispatch();
  const [duration, setDuration] = useState(0);

  // The express timer only runs in the new UX (hitl_3_0) with the express-timer
  // flag on. When active and the backend supplied a session start time, the
  // elapsed time is anchored to it; otherwise we keep the original behavior
  // (count from when the timer mounts).
  const expressActive = isHitl3 && isExpressTimerEnabled;

  const getFormatedDuration = () => {
    const minutes = Math.floor(duration / 60);
    const seconds = duration - minutes * 60;
    const secondsToString =
      seconds > 9 ? String(seconds) : '0' + String(seconds);
    return String(minutes) + ':' + secondsToString;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (sessionId) {
      // Anchor to the session start time from the task_assigned event when the
      // express timer is active and a valid timestamp is present; otherwise fall
      // back to the original mount-time anchor.
      const parsedStart = expressActive
        ? new Date(sessionStartedAt).getTime()
        : NaN;
      const startTime =
        !isNaN(parsedStart) && sessionStartedAt
          ? parsedStart
          : new Date().getTime();

      const getDuration = () => {
        const durationInSecond = Math.max(
          0,
          Math.floor((new Date().getTime() - startTime) / 1000)
        );
        setDuration(durationInSecond);
      };
      // Compute immediately so a past anchor shows the right value on first paint.
      getDuration();
      timer = setInterval(() => {
        getDuration();
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
        setDuration(0);
      }
    };
  }, [sessionId, expressActive, sessionStartedAt]);

  const formatted = getFormatedDuration();

  // Escalating alert: calm -> amber as the order nears the limit -> red + pulse
  // once it exceeds the limit. Computed before the early return so the latch
  // effect below keeps a stable hook order.
  const over = expressActive && maxSeconds > 0 && duration > maxSeconds;
  const nearing =
    expressActive &&
    maxSeconds > 0 &&
    !over &&
    duration >= Math.floor(maxSeconds * 0.8);

  // The Timer is the source of truth for elapsed time (it counts from the
  // task_assigned anchor when present, else from mount). When it crosses the
  // threshold, latch isExpressTimerElapsed so express mode — both the upsell
  // suppression and the header bolt indicator — engages in lockstep with the
  // red timer, even in sessions that never received a task_assigned
  // session_started_at (where useExpressTimer cannot anchor and so never
  // latches on its own).
  useEffect(() => {
    // Do not re-latch once the car has exited: session_started_at is not cleared
    // until the next assignment, so the still-past-limit duration would fight the
    // car-exit reset in useExpressTimer in an infinite render loop.
    if (over && !alreadyElapsed && !carExited) {
      dispatch(setExpressTimerElapsed(true));
    }
  }, [over, alreadyElapsed, carExited, dispatch]);

  // Flag-off / no-threshold path: render exactly as before (no behavior or
  // snapshot change when the express timer is not active).
  if (!expressActive || maxSeconds <= 0) {
    return <div className={classes.container}>{formatted}</div>;
  }

  const chip = (
    <Box
      className={classes.container}
      data-testid="express-timer"
      sx={{
        borderRadius: '4px',
        padding: '0 6px',
        fontWeight: nearing || over ? 700 : 400,
        ...(nearing && { color: Colors.amber }),
        ...(over && {
          color: Colors.white,
          backgroundColor: Colors.guardsmanRed,
          animation: `${expressTimerPulse} 1s ease-in-out infinite`,
        }),
      }}
    >
      {formatted}
    </Box>
  );

  if (over || nearing) {
    return (
      <Tooltip
        title={
          over
            ? `Over ${maxSeconds}s target`
            : `Approaching ${maxSeconds}s target`
        }
        arrow
      >
        {chip}
      </Tooltip>
    );
  }

  return chip;
};
